import type { AxiosInstance } from "axios";
import type { CreateOAuthSessionBody } from "@shared/schemas/oauth-session.schemas";
import type { AuthSessionResult } from "@shared/schemas/authentication.schemas";
import type { EmailAddress } from "@shared/schemas/email.schemas";

export interface LoginCredentials {
  readonly email: EmailAddress;

  readonly password: string;
}

export interface AuthApi {
  readonly getCurrentUser: () => Promise<unknown>;

  readonly logout: () => Promise<void>;

  readonly createSession: (token: string) => Promise<AuthSessionResult>;

  readonly login: (
    credentials: LoginCredentials,
    redirectUrl?: string,
  ) => Promise<AuthSessionResult>;

  readonly completeOAuthSession: (
    body: CreateOAuthSessionBody,
  ) => Promise<AuthSessionResult>;
}

export function createAuthApi(httpClient: AxiosInstance): AuthApi {
  return {
    getCurrentUser: async (): Promise<unknown> => {
      const response = await httpClient.get<unknown>("/api/v1/users/me");
      return response.data;
    },
    logout: async (): Promise<void> => {
      await httpClient.delete("/api/v1/auth/sessions");
    },
    createSession: async (token: string): Promise<AuthSessionResult> => {
      const response = await httpClient.post<AuthSessionResult>(
        "/api/v1/auth/sessions",
        { token },
        { params: { cookie: "true" } },
      );
      return response.data;
    },
    login: async (
      credentials: LoginCredentials,
      redirectUrl?: string,
    ): Promise<AuthSessionResult> => {
      const params: Record<string, string> = { cookie: "true" };
      if (redirectUrl !== undefined) {
        params["redirect"] = redirectUrl;
      }
      const response = await httpClient.post<AuthSessionResult>(
        "/api/v1/auth/login",
        credentials,
        { params },
      );
      return response.data;
    },
    completeOAuthSession: async (
      body: CreateOAuthSessionBody,
    ): Promise<AuthSessionResult> => {
      const response = await httpClient.post<AuthSessionResult>(
        "/api/v1/auth/sessions/oauth",
        body,
        { params: { cookie: "true" } },
      );
      return response.data;
    },
  };
}
